import React, { useState } from 'react'
import MDButton from 'components/MDButton'
import MDModal from 'components/MDModal'
import MDBox from 'components/MDBox'
import MDTypography from 'components/MDTypography'
import { useAppServices } from 'hook/services'
import {
  Button,
  Stack,
  LinearProgress,
  Alert,
  Snackbar,
  Divider,
  Paper,
} from '@mui/material'
import { Close, CloudUpload } from '@mui/icons-material'
import toast from 'react-hot-toast'

const UploadModal = ({ handleRefresh }) => {
  const AppService = useAppServices()
  const [open, setOpen] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [previews, setPreviews] = useState([])
  const [uploadedUrls, setUploadedUrls] = useState([])
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

  const handleOpen = () => {
    setOpen(true)
    setPreviews([])
    setUploadedUrls([])
  }

  const handleClose = () => {
    setOpen(false)
    setPreviews([])
    setUploadedUrls([])
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return

    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml']

    const valid = files.filter((file) => {
      if (!allowed.includes(file.type)) {
        setSnackbar({ open: true, message: `Invalid type: ${file.name}`, severity: 'error' })
        return false
      }
      if (file.size > 10 * 1024 * 1024) {
        setSnackbar({ open: true, message: `Too large (max 10MB): ${file.name}`, severity: 'error' })
        return false
      }
      return true
    })

    const newPreviews = valid.map((file) => ({
      file,
      previewUrl: URL.createObjectURL(file),
    }))

    setPreviews((prev) => [...prev, ...newPreviews])
    e.target.value = ''
  }

  const handleRemovePreview = (index) => {
    setPreviews((prev) => {
      URL.revokeObjectURL(prev[index].previewUrl)
      return prev.filter((_, i) => i !== index)
    })
  }

  const handleUpload = async () => {
    if (!previews.length) return
    setProcessing(true)
    const urls = []

    for (const { file } of previews) {
      try {
        const formData = new FormData()
        formData.append('file', file)

        const { response } = await AppService.files.upload({ payload: formData })

        if (response?.success) {
          urls.push(response.data?.fullUrl)
          toast.success(`${file.name} uploaded!`)
        } else {
          toast.error(`Failed: ${file.name}`)
        }
      } catch {
        toast.error(`Error uploading: ${file.name}`)
      }
    }

    setUploadedUrls(urls)
    setPreviews([])
    setProcessing(false)
    handleRefresh?.()
  }

  const handleCopyUrl = (url) => {
    navigator.clipboard.writeText(url)
    toast.success('URL copied!')
  }

  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 600,
    maxHeight: '85vh',
    overflowY: 'auto',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 3,
  }

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      <MDButton color="white" variant="contained" onClick={handleOpen}>
        Upload Files
      </MDButton>

      <MDModal open={open} onClose={handleClose}>
        <MDBox sx={style}>
          <MDTypography variant="h5" mb={3} fontWeight="bold">
            Upload Files
          </MDTypography>

          <Paper
            variant="outlined"
            sx={{
              p: 3,
              mb: 3,
              textAlign: 'center',
              border: '2px dashed #90caf9',
              borderRadius: 2,
              backgroundColor: '#f0f7ff',
              cursor: 'pointer',
            }}
            onClick={() => document.getElementById('file-input-upload').click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: '#1976d2', mb: 1 }} />
            <MDTypography variant="body2" color="text">
              Click to select images
            </MDTypography>
            <MDTypography variant="caption" color="text" sx={{ opacity: 0.6 }}>
              JPEG, PNG, WEBP, GIF, SVG — Max 10MB each
            </MDTypography>
            <input
              id="file-input-upload"
              hidden
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
            />
          </Paper>

          {previews.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <MDTypography variant="button" fontWeight="bold">
                Selected ({previews.length})
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                {previews.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      width: 80,
                      height: 80,
                      borderRadius: 8,
                      overflow: 'hidden',
                      border: '1px solid #ddd',
                    }}
                  >
                    <img
                      src={item.previewUrl}
                      alt="preview"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <button
                      onClick={() => handleRemovePreview(index)}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        background: 'rgba(0,0,0,0.6)',
                        border: 'none',
                        borderRadius: '50%',
                        width: 20,
                        height: 20,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                      }}
                    >
                      <Close sx={{ fontSize: 14, color: 'white' }} />
                    </button>
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: 'rgba(0,0,0,0.5)',
                        color: 'white',
                        fontSize: 9,
                        padding: '2px 4px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.file.name}
                    </div>
                  </div>
                ))}
              </Stack>
            </Paper>
          )}

          {processing && (
            <MDBox mb={2}>
              <MDTypography variant="caption" color="text">
                Uploading...
              </MDTypography>
              <LinearProgress sx={{ mt: 0.5 }} />
            </MDBox>
          )}

          {uploadedUrls.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, backgroundColor: '#f1f8e9' }}>
              <MDTypography variant="button" fontWeight="bold" color="success">
                ✅ Uploaded Successfully ({uploadedUrls.length})
              </MDTypography>
              <Divider sx={{ my: 1 }} />
              <Stack spacing={1}>
                {uploadedUrls.map((url, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}>
                    <span
                      style={{
                        flex: 1,
                        fontSize: 12,
                        color: '#1565c0',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {url}
                    </span>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopyUrl(url)}
                      sx={{ minWidth: 80, fontSize: 11 }}
                    >
                      Copy
                    </Button>
                  </Stack>
                ))}
              </Stack>
            </Paper>
          )}

          <MDBox display="flex" justifyContent="flex-end" gap={1} mt={2}>
            <MDButton variant="gradient" color="primary" onClick={handleClose}>
              Close
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleUpload}
              disabled={processing || previews.length === 0}
            >
              {processing ? 'Uploading...' : `Upload (${previews.length})`}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDModal>
    </>
  )
}

export default UploadModal